import React, { useState, useEffect } from 'react';
import { MapPin, Navigation, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { useGooglePlacesAutocomplete, reverseGeocode } from '@/hooks/useGooglePlacesAutocomplete';

interface Location {
  id: string;
  name: string;
  slug: string;
}

interface SearchLocationDropdownProps {
  value: string;
  onChange: (value: string) => void;
  onClose: () => void;
}

export const SearchLocationDropdown: React.FC<SearchLocationDropdownProps> = ({ 
  value, 
  onChange,
  onClose
}) => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [userCity, setUserCity] = useState<string | null>(null);
  const [nearbyLoading, setNearbyLoading] = useState(false);
  
  const { suggestions, isLoading: searchLoading, searchAddress, clearSuggestions } = useGooglePlacesAutocomplete();

  useEffect(() => {
    fetchLocations();
    loadUserLocation();
  }, []);

  useEffect(() => {
    if (value.length >= 2) {
      searchAddress(value);
    } else {
      clearSuggestions();
    }
  }, [value, searchAddress, clearSuggestions]);

  const fetchLocations = async () => {
    const { data } = await supabase
      .from('locations')
      .select('id, name, slug')
      .eq('is_active', true)
      .order('display_order')
      .limit(12);
    
    if (data) setLocations(data);
    setLoadingLocations(false);
  };

  const loadUserLocation = () => {
    const cachedCity = localStorage.getItem('userCity');
    if (cachedCity) {
      setUserCity(cachedCity);
    }
  };

  const handleSelect = (name: string) => {
    onChange(name);
    onClose();
    clearSuggestions();
  };

  const handleNearbyClick = async () => {
    setNearbyLoading(true);
    
    // Always get fresh location for nearby search
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const city = await reverseGeocode(
              position.coords.latitude,
              position.coords.longitude
            );
            
            // Store for display purposes
            if (city) {
              localStorage.setItem('userCity', city);
              setUserCity(city);
            }
            localStorage.setItem('userLat', position.coords.latitude.toString());
            localStorage.setItem('userLon', position.coords.longitude.toString());
            
            setNearbyLoading(false);
            
            // Always navigate with nearby params for proper distance-based search
            const params = new URLSearchParams();
            params.set('nearby', 'true');
            params.set('lat', position.coords.latitude.toString());
            params.set('lon', position.coords.longitude.toString());
            if (city) {
              params.set('location', city);
            }
            window.location.href = `/search?${params.toString()}`;
          } catch (error) {
            console.error('Error getting location:', error);
            setNearbyLoading(false);
            // Fallback to cached location if available
            const cachedLat = localStorage.getItem('userLat');
            const cachedLon = localStorage.getItem('userLon');
            if (cachedLat && cachedLon) {
              const params = new URLSearchParams();
              params.set('nearby', 'true');
              params.set('lat', cachedLat);
              params.set('lon', cachedLon);
              if (userCity) params.set('location', userCity);
              window.location.href = `/search?${params.toString()}`;
            } else {
              handleSelect('Nearby');
            }
          }
        },
        () => {
          setNearbyLoading(false);
          // Fallback to cached location if permission denied but we have cached coords
          const cachedLat = localStorage.getItem('userLat');
          const cachedLon = localStorage.getItem('userLon');
          if (cachedLat && cachedLon) {
            const params = new URLSearchParams();
            params.set('nearby', 'true');
            params.set('lat', cachedLat);
            params.set('lon', cachedLon);
            if (userCity) params.set('location', userCity);
            window.location.href = `/search?${params.toString()}`;
          } else {
            handleSelect('Nearby');
          }
        }
      );
    } else {
      setNearbyLoading(false);
      handleSelect('Nearby');
    }
  };

  return (
    <div className="p-4">
      {/* Nearby Option */}
      <button
        onClick={handleNearbyClick}
        disabled={nearbyLoading}
        className="flex items-center gap-4 w-full p-4 hover:bg-muted transition-colors rounded-xl mb-4 border border-border disabled:opacity-50"
      >
        <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
          {nearbyLoading ? (
            <Loader2 className="w-5 h-5 text-primary animate-spin" />
          ) : (
            <Navigation className="w-5 h-5 text-primary" />
          )}
        </div>
        <div className="text-left">
          <p className="font-medium">{nearbyLoading ? 'Finding your location...' : 'Nearby'}</p>
          <p className="text-sm text-muted-foreground">
            {userCity ? `Based on ${userCity}` : 'Find listings close to you'}
          </p>
        </div>
      </button>

      {/* Search Results */}
      {searchLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : suggestions.length > 0 ? (
        <div>
          <div className="px-2 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Search Results
          </div>
          <div className="grid grid-cols-2 gap-2">
            {suggestions.slice(0, 6).map((suggestion) => (
              <button
                key={suggestion.id}
                onClick={() => handleSelect(suggestion.text)}
                className="flex items-center gap-3 p-3 hover:bg-muted transition-colors rounded-lg text-left"
              >
                <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4" />
                </div>
                <div className="overflow-hidden min-w-0">
                  <p className="font-medium truncate text-sm">{suggestion.text}</p>
                  <p className="text-xs text-muted-foreground truncate">{suggestion.place_name}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      ) : (
        /* Featured Locations in Columns */
        <div>
          <div className="px-2 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Popular Destinations
          </div>
          {loadingLocations ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {locations.map((loc) => (
                <button
                  key={loc.id}
                  onClick={() => handleSelect(loc.name)}
                  className={cn(
                    "flex items-center gap-3 p-3 hover:bg-muted transition-colors rounded-lg",
                    value === loc.name && "bg-muted"
                  )}
                >
                  <div className="w-8 h-8 bg-muted rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4" />
                  </div>
                  <span className="font-medium text-sm truncate">{loc.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
